/**
 * Реестр полей визитки и разбор ответа сервера.
 *
 * Состав визитки задаёт сервер: GET /business-card возвращает объект `data`,
 * в котором лежат только поля, разрешённые на мероприятии. Интерфейс строится
 * по пересечению этого объекта с реестром — нет ключа `telegram` в ответе,
 * значит поля Telegram нет ни в форме, ни в просмотре. Значение при этом
 * роли не играет: пустая строка или пустой массив — поле показываем,
 * отсутствие ключа — не показываем.
 *
 * Каждое поле приходит обёрткой `{ value, readonly }`. `readonly: true` — значение
 * взято из профиля участника и в визитке не правится: показываем его строкой.
 *
 * Дополнительные поля мероприятия приходят иначе — описателями в `extend_data`
 * рядом с `data`, а выбранные значения лежат в `data.extend_data`. Разбор — в
 * parseExtendFields ниже.
 *
 * Не миксин, обычный модуль с функциями. Лежит рядом с methodsBusinessCard.js,
 * потому что относится к тому же разделу.
 */

/** Поля-строки — блок «Личные данные». */
export const TEXT_FIELDS = [
  {
    key: 'lname',
    labelKey: 'businessCardEdit.last_name',
    icon: 'exu-user',
  },
  {
    key: 'fname',
    labelKey: 'businessCardEdit.first_name',
    icon: 'exu-user',
  },
  {
    key: 'sname',
    labelKey: 'businessCardEdit.second_name',
    icon: 'exu-user',
  },
  {
    key: 'position',
    labelKey: 'businessCardEdit.position',
    icon: 'exu-briefcase',
  },
  {
    key: 'department',
    labelKey: 'businessCardEdit.department',
    icon: 'exu-building-07',
  },
  {
    key: 'company',
    labelKey: 'businessCardEdit.company',
    icon: 'exu-building-05',
  },
];

/** Поля-списки — блок «Контакты», у каждого своя кнопка «Добавить». */
export const LIST_FIELDS = [
  {
    key: 'phone',
    labelKey: 'businessCardEdit.phone',
    addLabelKey: 'businessCardEdit.add_phone',
    icon: 'exu-phone-1',
    component: 'phone-input',
    toHref: (v) => `tel:${v}`,
  },
  {
    key: 'email',
    label: 'E-mail',
    addLabelKey: 'businessCardEdit.add_email',
    icon: 'exu-mail-1',
    component: 'input-field',
    toHref: (v) => `mailto:${v}`,
  },
  {
    key: 'website',
    labelKey: 'businessCardEdit.website',
    addLabelKey: 'businessCardEdit.add_website',
    icon: 'exu-global',
    component: 'input-field',
    toHref: (v) => (v.startsWith('http') ? v : `https://${v}`),
  },
  {
    key: 'telegram',
    label: 'Telegram',
    addLabelKey: 'businessCardEdit.add_telegram',
    icon: 'exu-telegram-icon',
    component: 'input-field',
    toHref: (v) => `https://t.me/${v.replace(/^@/, '')}`,
  },
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    addLabelKey: 'businessCardEdit.add_whatsapp',
    icon: 'exu-whatsapp-icon',
    component: 'input-field',
    toHref: (v) => `https://wa.me/${v.replace(/\D/g, '')}`,
  },
];

/** Части имени — в порядке отображения: Фамилия Имя Отчество. */
export const NAME_KEYS = ['lname', 'fname', 'sname'];

function has(raw, key) {
  return Object.prototype.hasOwnProperty.call(raw, key);
}

/**
 * Поля в `data` приходят обёрткой `{ value, readonly }`: `readonly: true` значит,
 * что значение подставлено из профиля участника и правится не здесь.
 *
 * Разворачиваем обёртку сразу при разборе, чтобы весь остальной код работал с
 * обычными значениями. Голое значение (без обёртки) тоже принимаем — на случай
 * стенда, где поле ещё не переведено на новый формат.
 *
 * Разворачиваем в цикле: если поле уже успело сохраниться обёрнутым, в нём
 * лежит обёртка в обёртке, и снять надо все слои сразу — иначе каждое
 * сохранение чистило бы по одному.
 */
export function fieldValue(entry) {
  let value = entry;
  while (value && typeof value === 'object' && !Array.isArray(value) && has(value, 'value')) {
    value = value.value;
  }
  return value;
}

function isReadonly(entry) {
  return !!(entry && typeof entry === 'object' && !Array.isArray(entry) && entry.readonly);
}

/**
 * Имя одной строкой — для заголовка визитки и списка коллекции.
 * Принимает и `card.values`, и сырой `card_data` из ответа сервера.
 *
 * `fullName` в запасе — на случай стенда, который ещё не разделил имя на части.
 * Когда все мероприятия переедут, ветку можно убрать.
 */
export function personName(source) {
  if (!source || typeof source !== 'object') return '';
  const parts = NAME_KEYS
    .map((key) => fieldValue(source[key]))
    .filter((v) => typeof v === 'string' && v);
  return parts.join(' ') || fieldValue(source.fullName) || '';
}

/** Ключ, под которым выбранные значения дополнительных полей лежат внутри `data`. */
export const EXTEND_KEY = 'extend_data';

/** Типы описателей, которые клиент умеет рисовать. `multi` — выбор галочками. */
const EXTEND_TYPES = ['multi', 'group'];

function hasField(raw, field) {
  return has(raw, field.key);
}

function toList(value) {
  if (Array.isArray(value)) return value.map((v) => (v == null ? '' : String(v)));
  // одиночное значение строкой — приводим к списку, чтобы форма работала одинаково
  if (typeof value === 'string' && value) return [value];
  return [];
}

/**
 * Подпись из описателя: сервер шлёт её сразу на двух языках — `label_ru` и
 * `label_en`. Берём текущий язык, при его отсутствии — любую заполненную.
 */
function pickLabel(source, lang) {
  if (!source || typeof source !== 'object') return '';
  return source[`label_${lang}`] || source.label || source.label_ru || source.label_en || '';
}

/**
 * Разбирает описатели дополнительных полей — массив `extend_data`, который
 * лежит в ответе рядом с `data`.
 *
 * Состав здесь задаёт описатель, а не ключи в `data`: организатор может
 * добавить поле после того, как участник сохранил визитку, и тогда ключа в его
 * `data.extend_data` ещё нет. Считать такое поле отсутствующим значило бы, что
 * заполнить его уже нельзя.
 *
 * Возвращает только поля известных типов. Чужие типы пропускаем, но их
 * значения всё равно уходят обратно нетронутыми — см. toPayload.
 */
export function parseExtendFields(descriptors, lang = 'ru') {
  if (!Array.isArray(descriptors)) return [];
  return descriptors
    .filter((d) => d && typeof d === 'object' && d.name && EXTEND_TYPES.includes(d.type))
    .map((d) => ({
      name: d.name,
      type: d.type,
      label: pickLabel(d, lang),
      options: (Array.isArray(d.values) ? d.values : [])
        .filter((v) => v && typeof v === 'object' && v.id != null)
        .map((v) => ({ id: v.id, label: pickLabel(v, lang) })),
    }));
}

/** Пустая визитка: полей нет, пока не пришёл ответ сервера. */
export function emptyCard() {
  return {
    textFields: [],
    listFields: [],
    extendFields: [],
    values: {},
    // ключ поля → true, если сервер отдал его с readonly: правится не в визитке
    readonly: {},
    extendValues: {},
    // исходный `data.extend_data`: вернём в PUT ключи, которых нет в описателях
    extendRaw: {},
    hasExtend: false,
    extra: {},
  };
}

/**
 * Разбирает `data` из ответа сервера в модель визитки.
 * Возвращает null, если пришёл не объект — вызывающий показывает system-card.
 *
 * `descriptors` — массив `extend_data` из ответа сервера, `lang` — текущий язык
 * интерфейса (`$store.getters.getLang`).
 *
 * В модели:
 * - textFields / listFields — только пришедшие поля, в порядке реестра;
 * - values — их значения;
 * - extendFields — дополнительные поля мероприятия, extendValues — их выбор;
 * - extra — неизвестные реестру поля: не показываем, но возвращаем в PUT;
 *   обёртку с них снимаем так же, как с известных.
 */
export function parseCard(raw, descriptors = null, lang = 'ru') {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const card = emptyCard();

  TEXT_FIELDS.forEach((field) => {
    if (!hasField(raw, field)) return;
    const value = fieldValue(raw[field.key]);
    card.textFields.push(field);
    card.values[field.key] = typeof value === 'string' ? value : '';
    card.readonly[field.key] = isReadonly(raw[field.key]);
  });

  LIST_FIELDS.forEach((field) => {
    if (!hasField(raw, field)) return;
    card.listFields.push(field);
    card.values[field.key] = toList(fieldValue(raw[field.key]));
    card.readonly[field.key] = isReadonly(raw[field.key]);
  });

  const rawExtend = fieldValue(raw[EXTEND_KEY]);
  if (rawExtend && typeof rawExtend === 'object' && !Array.isArray(rawExtend)) {
    card.extendRaw = { ...rawExtend };
  }
  card.extendFields = parseExtendFields(descriptors, lang);
  card.extendFields.forEach((field) => {
    const selected = card.extendRaw[field.name];
    card.extendValues[field.name] = Array.isArray(selected) ? [...selected] : [];
  });
  card.hasExtend = has(raw, EXTEND_KEY) || card.extendFields.length > 0;

  // Неизвестные реестру поля не показываем, но возвращаем серверу. Обёртку с них
  // снимаем так же, как с известных: PUT ждёт одно значение, и если вернуть
  // { value, readonly } целиком, сервер запишет объект как значение и на
  // следующем GET обернёт его ещё раз — поле обрастает слоями с каждым сохранением.
  Object.keys(raw).forEach((key) => {
    if (key === EXTEND_KEY) return;
    if (!(key in card.values)) card.extra[key] = fieldValue(raw[key]);
  });

  return card;
}

/**
 * Собирает тело PUT /business-card/: те же ключи, что прислал сервер,
 * плюс неизвестные поля. Пустые строки в списках отбрасываем —
 * это строки, которые пользователь добавил и не заполнил.
 *
 * Значения уходят голыми, без обёртки `{ value, readonly }`, в которой их
 * присылает GET. Поля с `readonly` тоже уходят — с тем же значением, какое
 * пришло: пользователь их не менял, а отбросить их значило бы стереть данные,
 * если сервер сохраняет `data` целиком.
 *
 * Выбор в дополнительных полях уходит в `extend_data` массивом
 * идентификаторов: `{ "extend_data": { "category": [1, 3] } }`.
 */
export function toPayload(card) {
  const payload = { ...card.extra };
  card.textFields.forEach((field) => {
    payload[field.key] = card.values[field.key];
  });
  card.listFields.forEach((field) => {
    payload[field.key] = (card.values[field.key] || []).filter((v) => v);
  });
  if (card.hasExtend) {
    // ключи без описателя (чужой или новый тип) возвращаем как пришли
    payload[EXTEND_KEY] = { ...card.extendRaw };
    card.extendFields.forEach((field) => {
      payload[EXTEND_KEY][field.name] = [...(card.extendValues[field.name] || [])];
    });
  }
  return payload;
}

/**
 * Группы контактов для просмотра: поля-списки с непустыми значениями.
 * `label` приходит от компонента, потому что зависит от $t.
 */
export function contactGroups(card, label) {
  return card.listFields
    .map((field) => ({
      key: field.key,
      icon: field.icon,
      label: label(field),
      toHref: field.toHref,
      values: (card.values[field.key] || []).filter((v) => v),
    }))
    .filter((group) => group.values.length);
}

/**
 * Дополнительные поля для просмотра: подписи выбранных вариантов.
 * Поля без выбора не показываем — как и контакты без значений.
 */
export function extendGroups(card) {
  return card.extendFields
    .map((field) => {
      const selected = card.extendValues[field.name] || [];
      return {
        name: field.name,
        label: field.label,
        values: field.options
          .filter((option) => selected.some((id) => String(id) === String(option.id)))
          .map((option) => option.label),
      };
    })
    .filter((group) => group.values.length);
}
